import { Fragment } from "react";
import { Link } from "@tanstack/react-router";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  emptyAnunciosFilters,
  type AnunciosFilters,
} from "@/components/admin/AnunciosFiltersForm";
import { encodePortalUrl, type PortalLookups } from "@/lib/portal-url";

export function PortalBreadcrumbs({
  mode,
  filters,
  lookups,
  basePath = "/portal",
}: {
  mode: "all" | "favoritos" | "descartados" | "vip";
  filters: AnunciosFilters;
  lookups: PortalLookups;
  basePath?: string;
}) {
  const portalLabel = mode === "vip" ? "Portal VIP" : "Portal";
  const items: { label: string; to?: string }[] = [
    { label: "Inicio", to: "/" },
    { label: portalLabel, to: basePath },
  ];

  if (mode === "favoritos") {
    items.push({ label: "Favoritos", to: `${basePath}/favoritos` });
  } else if (mode === "descartados") {
    items.push({ label: "Descartados", to: `${basePath}/descartados` });
  } else {
    const partial: AnunciosFilters = { ...emptyAnunciosFilters };

    const levels = [
      {
        ids: filters.operacion_ids,
        lookup: lookups.operaciones,
        fallback: "Operación",
        assign: (ids: string[]) => {
          partial.operacion_ids = ids;
        },
      },
      {
        ids: filters.uso_local_ids,
        lookup: lookups.usos,
        fallback: "Uso",
        assign: (ids: string[]) => {
          partial.uso_local_ids = ids;
        },
      },
      {
        ids: filters.provincia_ids,
        lookup: lookups.provincias,
        fallback: "Provincia",
        assign: (ids: string[]) => {
          partial.provincia_ids = ids;
        },
      },
      {
        ids: filters.ciudad_ids,
        lookup: lookups.ciudades,
        fallback: "Ciudad",
        assign: (ids: string[]) => {
          partial.ciudad_ids = ids;
        },
      },
      {
        ids: filters.barrio_ids,
        lookup: lookups.barrios,
        fallback: "Barrio",
        assign: (ids: string[]) => {
          partial.barrio_ids = ids;
        },
      },
    ];

    for (const level of levels) {
      if (level.ids.length === 1) {
        level.assign(level.ids);
        const name =
          level.lookup.find((x) => x.id === level.ids[0])?.nombre ??
          level.fallback;
        const { pathname } = encodePortalUrl(partial, lookups, basePath);
        items.push({ label: name, to: pathname });
      }
    }
  }

  return (
    <Breadcrumb className="mb-4">
      <BreadcrumbList>
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <Fragment key={i}>
              <BreadcrumbItem>
                {isLast || !item.to ? (
                  <BreadcrumbPage>{item.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={item.to as never}>{item.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </Fragment>
          );
        })}

      </BreadcrumbList>
    </Breadcrumb>
  );
}
